import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import { FormattedNumber } from 'react-intl';

import { Button, Drawer, Typography } from 'antd';

import MyCollapse from 'components/Collapse';
import FileSelector from 'components/FileSelector';
import type { FileSelectorHandle } from 'components/FileSelector';
import MaskedOutput from 'components/MaskedOutput';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useFiscalDocumentsQuickView } from 'v2/hooks/useFiscalDocumentsQuickView';

import Customer from './Customer';
import Files from './Files';
import Items from './Items';
import { useFiscalDocuments } from '../../context';

const EDUZZ_EMITTER_IDS = [70042526, 52584, 52586, 63545352];

const QuickView: React.FC = () => {
  const fileSelector = useRef<FileSelectorHandle>(null);

  const { selectedData, setSelectedData } = useFiscalDocuments();
  const active = useMemo(() => Boolean(selectedData), [selectedData]);

  const { upload, uploadIsPending, download, downloadIsPending } = useFiscalDocumentsQuickView();

  const handleClose = useCallback(() => {
    setSelectedData(undefined);
  }, [setSelectedData]);

  const handleSelectFile = useCallback(async () => {
    const file = await fileSelector.current.open();

    if (!file) {
      return;
    }

    upload(file);
  }, [upload]);

  const handleClickDownload = useCallback(async () => {
    download(selectedData?.id);
  }, [download, selectedData?.id]);

  return (
    <Fragment>
      <FileSelector ref={fileSelector} />

      <Drawer
        onClose={handleClose}
        placement='right'
        open={active}
        closable
        width={450}
        rootClassName='max-[1024px]:[&_.ant-drawer-content-wrapper]:w-full!'
        title={`COD. ${selectedData?.id}`}
      >
        {active && (
          <>
            <Typography.Text className='m-0!'>
              {selectedData.status} -&nbsp;
              <MaskedOutput mask='date' value={selectedData.reference_date} />
            </Typography.Text>
            <Typography.Title level={5} className='m-0!'>
              {selectedData.display}
            </Typography.Title>
            <Typography.Text className='m-0!'>
              <FormattedNumber value={selectedData.value || 0} format='BRL' />
            </Typography.Text>

            <div className='mt-6' />

            <MyCollapse title='Origem' arrow>
              <Customer data={selectedData.customer_origin} />
            </MyCollapse>

            <div className='mt-2' />

            <MyCollapse title='Destino' arrow>
              <Customer data={selectedData.destination_customer} />
            </MyCollapse>

            <div className='mt-2' />

            <MyCollapse title='Detalhes' arrow>
              <Items />
            </MyCollapse>

            <div className='mt-2' />

            <MyCollapse title='Arquivos' arrow>
              <Files />
            </MyCollapse>

            {!EDUZZ_EMITTER_IDS.includes(selectedData.customer_origin.id) && (
              <PermissionHide action='myeduzz:cdf:editar'>
                <div className='mt-2' />

                <div className='text-right'>
                  <Button disabled={uploadIsPending} onClick={handleSelectFile} loading={uploadIsPending}>
                    {uploadIsPending ? 'Enviando...' : 'Upload de Arquivo'}
                  </Button>
                </div>
              </PermissionHide>
            )}

            <div className='mt-2' />

            {EDUZZ_EMITTER_IDS.includes(selectedData.customer_origin.id) && (
              <div className='text-right'>
                <Button disabled={downloadIsPending} onClick={handleClickDownload} loading={downloadIsPending}>
                  {downloadIsPending ? 'Baixando...' : 'Download'}
                </Button>
              </div>
            )}
          </>
        )}
      </Drawer>
    </Fragment>
  );
};

export default QuickView;
